'use client'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Globe, Loader2 } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import {
  locales,
  localeNames,
  localeFlags,
  defaultLocale,
  type Locale,
} from '@/lib/i18n/config'
import { createClient } from '@/lib/supabase/client'

/**
 * Strip any locale prefix from a path. `/es/blog` → `/blogs`,
 * `/pt` → `/`, `/blogs` → `/blogs`. Used so we can re-prefix with
 * the newly-chosen locale when the user picks one.
 *
 * The lookahead `(?=...)` matches the separator without consuming
 * it; without that, `/pt` (no trailing slash) had `m[0].length`
 * = 3 and `path.slice(2)` returned `'t'` — keeping the last
 * letter of the locale and producing URLs like `/jat` when
 * switching from `/pt` to Japanese.
 */
function stripLocalePrefix(path: string): string {
  const m = path.match(/^\/([a-z]{2})(?=\/|$)/)
  if (m && (locales as readonly string[]).includes(m[1])) {
    return path.slice(m[0].length) || '/'
  }
  return path
}

/**
 * Build the locale-prefixed equivalent of `path`. Default locale
 * stays un-prefixed (matches `localePrefix: 'as-needed'`).
 */
function buildLocalizedPath(path: string, locale: Locale): string {
  const bare = stripLocalePrefix(path)
  if (locale === defaultLocale) return bare
  return `/${locale}${bare === '/' ? '' : bare}`
}

/**
 * Variant of the switcher.
 *
 *   • 'header'    — inline icon button next to login/avatar in the
 *                   site header. Dropdown opens beneath, right-aligned.
 *   • 'floating'  — fixed-positioned bubble in a screen corner.
 *                   Position (left vs right) is controlled by the
 *                   `corner` prop. Dropdown opens above the bubble.
 */
type Variant = 'header' | 'floating'
type Corner = 'left' | 'right'

interface LanguageSwitcherProps {
  variant?: Variant
  /** Only used when variant is 'floating'. Defaults to 'right'. */
  corner?: Corner
}

/**
 * Public-site language switcher.
 *
 * Source of truth is `profiles.locale`. The switcher posts the new
 * value to /api/profile/locale and refreshes the page so the next
 * request — and every server-rendered string + Sanity locale read on
 * it — picks up the change.
 *
 * For signed-out visitors there's no profile to update. Their locale
 * comes from the `Accept-Language` header on the server side; we keep
 * the dropdown visible so they can see the language list, but a click
 * routes them to `/auth?redirect=...` so the choice can be saved.
 *
 * Deliberately no cookies / no localStorage — TheDuchy serves a heavy
 * EU audience and we keep the public site cookie-light to avoid GDPR
 * consent friction.
 */
export function LanguageSwitcher({
  variant = 'header',
  corner = 'right',
}: LanguageSwitcherProps) {
  const router = useRouter()
  const t = useTranslations('languageSwitcher')
  // The active locale comes from next-intl, which reads it on the
  // server from profile / Accept-Language. We don't track it locally.
  const currentLocale = useLocale() as Locale
  const [isOpen, setIsOpen] = useState(false)
  const [pending, setPending] = useState<Locale | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [authChecked, setAuthChecked] = useState(false)
  const [isAuthed, setIsAuthed] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Lazy auth probe — we only need to know "auth or not" to decide
  // whether a click persists or bumps to login. `getSession` is a
  // local-cache read, not a network call.
  useEffect(() => {
    let cancelled = false
    const supabase = createClient()
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return
      setIsAuthed(Boolean(data.session))
      setAuthChecked(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Click-outside dismissal.
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen])

  const handleChange = async (locale: Locale) => {
    setError(null)
    if (locale === currentLocale) {
      setIsOpen(false)
      return
    }

    const currentPath =
      typeof window === 'undefined'
        ? '/'
        : window.location.pathname + window.location.search
    const targetPath = buildLocalizedPath(currentPath, locale)

    // Anonymous visitor: route to login with a redirect back to the
    // already-localized target so they land in their chosen language
    // post-signup.
    if (authChecked && !isAuthed) {
      setIsOpen(false)
      router.push(
        `/auth?mode=register&redirect=${encodeURIComponent(targetPath)}&locale=${locale}`,
      )
      return
    }

    setPending(locale)
    try {
      const res = await fetch('/api/profile/locale', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ locale }),
      })
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}))
        throw new Error(
          (payload as { error?: string }).error || `Failed (${res.status})`,
        )
      }
      setIsOpen(false)
      // Navigate to the locale-prefixed equivalent of the current URL
      // (default locale stays un-prefixed). router.replace so the
      // back-button doesn't bounce to the previous-locale URL.
      router.replace(targetPath)
      router.refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorSaveFailed'))
    } finally {
      setPending(null)
    }
  }

  const isFloating = variant === 'floating'

  const wrapperPositionClass = isFloating
    ? `fixed bottom-5 z-50 ${corner === 'right' ? 'right-5' : 'left-5'}`
    : 'relative'

  const triggerClass = isFloating
    ? 'flex h-12 w-12 items-center justify-center bg-white text-gray-700 shadow-lg ring-1 ring-gray-200 transition hover:bg-gray-50 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:bg-gray-900 dark:text-gray-200 dark:ring-gray-800 dark:hover:bg-gray-800'
    : 'flex items-center gap-1.5 px-2 py-1.5 text-sm text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white'

  const dropdownPositionClass = isFloating
    ? `absolute bottom-full mb-2 w-60 ${corner === 'right' ? 'right-0' : 'left-0'}`
    : 'absolute right-0 top-full mt-1 w-56'

  return (
    <div ref={containerRef} className={wrapperPositionClass}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className={triggerClass}
        aria-label={t('ariaLabel')}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className={isFloating ? 'h-5 w-5' : 'h-4 w-4'} />
        {!isFloating && (
          <span className="hidden sm:inline" aria-hidden="true">
            {localeFlags[currentLocale]}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          role="listbox"
          aria-label="Language"
          className={`${dropdownPositionClass} z-50 overflow-hidden border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-800 dark:bg-gray-900`}
        >
          {authChecked && !isAuthed && (
            <p className="px-3 py-2 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
              {t('anonymousHint')}
            </p>
          )}
          {locales.map((locale) => {
            const isActive = currentLocale === locale
            const isPending = pending === locale
            return (
              <button
                key={locale}
                type="button"
                role="option"
                aria-selected={isActive}
                disabled={pending !== null}
                onClick={() => handleChange(locale)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  isActive
                    ? 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                    : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800'
                }`}
              >
                <span aria-hidden="true">{localeFlags[locale]}</span>
                {/* Each language name is written in its own language, so mark it
                    with lang= (WCAG 3.1.2) — otherwise a screen reader announces
                    e.g. "日本語" / "Español" in the page language's voice. */}
                <span className="flex-1" lang={locale}>
                  {localeNames[locale]}
                </span>
                {isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              </button>
            )
          })}
          {error && (
            <p
              role="alert"
              className="border-t border-gray-200 px-3 py-2 text-[11px] leading-snug text-red-600 dark:border-gray-800 dark:text-red-400"
            >
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
